/**
 * Shared MSP/user scope state for AI Thread Observer and Activity Report.
 *
 * MSP and user filters are independent. MSP changes refetch the user option list and
 * only prune invalid user picks; user changes never touch MSP scope.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type ObserverApi from '../apis/observerApi';
import {
  OBSERVER_SCOPE_ALL,
  type ObserverScope,
  mspScopeToRequestIds,
  pruneScope,
  scopeFromSelection,
  scopeToSelection,
  userScopeToRequestIds,
} from '../utils/observerScope';

export interface ObserverScopeMsp {
  id: number;
  name: string;
}

export interface ObserverScopeUser {
  id: string;
  email: string;
}

export interface UseObserverScopeOptions {
  /** When false, skip loading users for the MSP scope (e.g. while another Observer tab is active). */
  fetchUsers?: boolean;
  defaultOmitInternalUsers?: boolean;
  defaultOmitAutoagent?: boolean;
}

export function useObserverScope(
  msps: ObserverScopeMsp[],
  api: ObserverApi,
  options: UseObserverScopeOptions = {},
) {
  const fetchUsersEnabled = options.fetchUsers !== false;
  const [mspScope, setMspScope] = useState<ObserverScope>(OBSERVER_SCOPE_ALL);
  const [userScope, setUserScope] = useState<ObserverScope>(OBSERVER_SCOPE_ALL);
  const [users, setUsers] = useState<ObserverScopeUser[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersFetchError, setUsersFetchError] = useState<string | null>(null);
  const [omitInternalUsers, setOmitInternalUsers] = useState(
    () => options.defaultOmitInternalUsers === true,
  );
  const [omitAutoagent, setOmitAutoagent] = useState(
    () => options.defaultOmitAutoagent === true,
  );

  const allMspIds = useMemo(
    () => msps.map((msp) => msp.id).filter((id) => Number.isInteger(id) && id > 0),
    [msps],
  );

  const allMspValues = useMemo(
    () => allMspIds.map((id) => String(id)),
    [allMspIds],
  );

  const userEmails = useMemo(() => users.map((user) => user.email), [users]);

  const selectedMspIds = useMemo(
    () => scopeToSelection(mspScope, allMspValues),
    [mspScope, allMspValues],
  );

  const selectedUserIds = useMemo(
    () => scopeToSelection(userScope, userEmails),
    [userScope, userEmails],
  );

  const mspIdsForRequest = useMemo(
    () => mspScopeToRequestIds(allMspIds, mspScope),
    [allMspIds, mspScope],
  );

  const mspIdsKey = useMemo(
    () => [...mspIdsForRequest].sort((a, b) => a - b).join(','),
    [mspIdsForRequest],
  );

  const userIdsForRequest = useMemo(() => {
    const scopedUserIds = userScopeToRequestIds(userScope);
    if ((omitInternalUsers || omitAutoagent) && scopedUserIds === undefined) {
      return userEmails.length > 0 ? userEmails : null;
    }
    return scopedUserIds;
  }, [omitAutoagent, omitInternalUsers, userEmails, userScope]);

  const showMspColumn = useMemo(() => {
    if (mspScope.mode === 'all') {
      return allMspIds.length > 1;
    }
    return mspIdsForRequest.length > 1;
  }, [allMspIds.length, mspIdsForRequest.length, mspScope.mode]);

  const handleMspChange = useCallback((nextMspIds: string[]) => {
    setMspScope(scopeFromSelection(allMspValues, nextMspIds));
  }, [allMspValues]);

  const handleUserChange = useCallback((nextUserIds: string[]) => {
    setUserScope(scopeFromSelection(userEmails, nextUserIds));
  }, [userEmails]);

  useEffect(() => {
    if (!fetchUsersEnabled) {
      return;
    }

    let cancelled = false;

    const fetchUsers = async () => {
      if (mspIdsForRequest.length === 0) {
        setUsers([]);
        setUsersLoading(false);
        return;
      }

      // Do not display options from the previous MSP scope while this request is in flight.
      setUsers([]);
      setUsersLoading(true);
      setUsersFetchError(null);
      try {
        const fetchedUsers = await api.getUsers(mspIdsForRequest, omitInternalUsers, omitAutoagent);
        if (cancelled) {
          return;
        }
        const emails = fetchedUsers.map((user) => user.email);
        setUsers(fetchedUsers);
        setUserScope((current) => pruneScope(current, emails));
      } catch (err) {
        if (cancelled) {
          return;
        }
        setUsers([]);
        setUsersFetchError(
          err instanceof Error ? err.message : 'Could not load users for the selected MSP scope.',
        );
      } finally {
        if (!cancelled) {
          setUsersLoading(false);
        }
      }
    };

    fetchUsers();

    return () => {
      cancelled = true;
    };
  }, [api, fetchUsersEnabled, mspIdsKey, mspIdsForRequest, omitAutoagent, omitInternalUsers]);

  return {
    mspScope,
    userScope,
    selectedMspIds,
    selectedUserIds,
    users,
    usersLoading,
    usersFetchError,
    setUsersFetchError,
    mspIdsForRequest,
    userIdsForRequest,
    omitInternalUsers,
    omitAutoagent,
    showMspColumn,
    handleMspChange,
    handleUserChange,
    setOmitInternalUsers,
    setOmitAutoagent,
  };
}
